import * as THREE from 'three';

// ── RELIEF FROM THE PAINT ────────────────────────────────────────────────────
//
// The city's atlases are colour only — no normal maps, no roughness maps — so
// every wall is a photograph of a wall, flat under any light. Generating new
// textures to replace them cannot work: each atlas is a packed sheet whose
// regions only make sense through the model's own UVs.
//
// So the relief is DERIVED from the atlas itself: a Sobel pass over its
// brightness on the GPU, written to a normal map the same size, read through
// the same UVs — aligned by construction. Mortar lines sink, bricks stand
// proud, render and paving get grain, and a low sun now rakes across them.
//
// It is a classic approximation (brightness is not height), so it is applied
// gently and NOT to signage or glowing materials, where lettering would
// emboss. Desktop tiers only: each map costs a full-size render target in VRAM.

const VERT = /* glsl */`
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const FRAG = /* glsl */`
uniform sampler2D map;
uniform vec2 texel;
uniform float strength;
varying vec2 vUv;
float h(vec2 o) { return dot(texture2D(map, vUv + o * texel).rgb, vec3(0.299, 0.587, 0.114)); }
void main() {
  float tl = h(vec2(-1.0, 1.0)); float t = h(vec2(0.0, 1.0)); float tr = h(vec2(1.0, 1.0));
  float l = h(vec2(-1.0, 0.0));                                float r = h(vec2(1.0, 0.0));
  float bl = h(vec2(-1.0, -1.0)); float b = h(vec2(0.0, -1.0)); float br = h(vec2(1.0, -1.0));
  float dx = (tr + 2.0 * r + br) - (tl + 2.0 * l + bl);
  float dy = (tl + 2.0 * t + tr) - (bl + 2.0 * b + br);
  vec3 n = normalize(vec3(-dx * strength, -dy * strength, 1.0));
  gl_FragColor = vec4(n * 0.5 + 0.5, 1.0);
}
`;

let quad = null;
function getQuad() {
  if (quad) return quad;
  const mat = new THREE.ShaderMaterial({
    uniforms: { map: { value: null }, texel: { value: new THREE.Vector2() }, strength: { value: 2 } },
    vertexShader: VERT, fragmentShader: FRAG, depthTest: false, depthWrite: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  mesh.frustumCulled = false;
  const scene = new THREE.Scene(); scene.add(mesh);
  quad = { mat, scene, cam: new THREE.Camera() };
  return quad;
}

const cache = new Map();   // source texture uuid → derived texture

/** A normal map derived from `tex`'s brightness, same size and wrapping. */
export function deriveNormalMap(gl, tex, strength = 2) {
  if (!tex) return null;
  if (cache.has(tex.uuid)) return cache.get(tex.uuid);
  const w = tex.image?.width; const h = tex.image?.height;
  if (!w || !h) return null;
  const rt = new THREE.WebGLRenderTarget(w, h, {
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
    magFilter: THREE.LinearFilter,
    wrapS: tex.wrapS, wrapT: tex.wrapT,
    depthBuffer: false,
  });
  rt.texture.anisotropy = tex.anisotropy || 1;
  const q = getQuad();
  q.mat.uniforms.map.value = tex;
  q.mat.uniforms.texel.value.set(1 / w, 1 / h);
  q.mat.uniforms.strength.value = strength;
  const prev = gl.getRenderTarget();
  const prevShadow = gl.shadowMap.autoUpdate;
  gl.shadowMap.autoUpdate = false;
  gl.setRenderTarget(rt);
  gl.render(q.scene, q.cam);
  gl.setRenderTarget(prev);
  gl.shadowMap.autoUpdate = prevShadow;
  q.mat.uniforms.map.value = null;
  cache.set(tex.uuid, rt.texture);
  return rt.texture;
}

/**
 * Give every eligible material in `root` a derived normal map. Signage and
 * anything self-lit is left flat. Returns how many materials gained relief.
 */
export function applyDerivedNormals(gl, root, { scale = 0.7 } = {}) {
  const seen = new Set();
  let n = 0;
  root.traverse((o) => {
    if (!o.isMesh) return;
    for (const m of (Array.isArray(o.material) ? o.material : [o.material])) {
      if (!m || seen.has(m)) continue;
      seen.add(m);
      if (!m.isMeshStandardMaterial || !m.map || m.normalMap || m.emissiveMap || m.transparent) continue;
      if (/glow|sign|glass/i.test(m.name || '')) continue;
      const nm = deriveNormalMap(gl, m.map, 2.2);
      if (!nm) continue;
      m.normalMap = nm;
      m.normalScale.set(scale, scale);
      m.needsUpdate = true;
      n += 1;
    }
  });
  return n;
}
